// Clients feature screen (task 3.6) — Req 2.
//
// The electrician's customers and their job sites. Wired to the Phase 2 clients
// handler (api/handlers/clients.js):
//   GET  /clients[?includeArchived=true]   list
//   GET  /clients/{id}                      detail
//   POST /clients                           create
//   PUT  /clients/{id}                      edit / archive (archived flag)
// plus GET /clients/{id}/jobs to show a client's jobs on their detail screen.
//
// Routes handled (from the shell router): list ('#/clients'), new
// ('#/clients/new') and view ('#/clients/<id>'). Editing is done in place on the
// detail screen (the router has no separate edit action), and archiving is a soft
// flag so history is retained (Req 2.4).

import { api } from '../api.js';
import {
  h, mount, runAsync, emptyView, routeToHash,
  screenEl, card, listRow, detailList, sectionHeading, muted,
  field, makeInput, makeTextarea, errorSlot, setError,
  button, linkButton, buttonRow, repeatableList, runAction, goTo,
  packJobId, statusBadge, shortDate,
} from './ui.js';

const clientHash = (id) => routeToHash({ section: 'clients', action: 'view', id });

// --- List ------------------------------------------------------------------

export function clientsListScreen() {
  const body = h('div');
  let includeArchived = false;

  const load = () =>
    runAsync(
      body,
      () => api.get(`/clients${includeArchived ? '?includeArchived=true' : ''}`),
      (res) => renderList(res.items || []),
      { loadingMessage: 'Loading clients…' },
    );

  const renderList = (items) => {
    if (items.length === 0) {
      return emptyView({
        message: includeArchived ? 'No clients yet.' : 'No active clients yet.',
        actionLabel: 'New client',
        onAction: () => goTo({ section: 'clients', action: 'new' }),
      });
    }
    const rows = items.map((c) =>
      listRow({
        href: clientHash(c.id),
        title: c.name,
        subtitle: [c.contact && c.contact.phone, c.contact && c.contact.email].filter(Boolean).join(' · '),
        meta: c.archived ? statusBadge('archived') : null,
      }),
    );
    return h('div', { class: 'list' }, rows);
  };

  const archiveToggle = h('label', { class: 'checkbox-field toolbar-toggle' }, [
    (() => {
      const cb = h('input', { class: 'checkbox', type: 'checkbox' });
      cb.addEventListener('change', () => {
        includeArchived = cb.checked;
        load();
      });
      return cb;
    })(),
    h('span', { text: 'Show archived' }),
  ]);

  load();

  return screenEl('Clients', [archiveToggle, body], {
    section: 'clients',
    actions: linkButton('＋ New', routeToHash({ section: 'clients', action: 'new' }), { variant: 'primary', small: true }),
  });
}

// --- Shared client form (create + edit) ------------------------------------

// Build the client form controls from an (optional) existing client. Returns the
// form body node plus a read() that assembles the API payload, and a validate().
function clientForm(existing = {}) {
  const nameInput = makeInput({ value: existing.name || '', required: true, placeholder: 'e.g. Jane Smith' });
  const phoneInput = makeInput({ type: 'tel', value: (existing.contact && existing.contact.phone) || '', inputmode: 'tel', placeholder: '07…' });
  const emailInput = makeInput({ type: 'email', value: (existing.contact && existing.contact.email) || '', inputmode: 'email', placeholder: 'name@example.co.uk' });
  const billingInput = makeTextarea({ value: existing.billingAddress || '', rows: 3, placeholder: 'Billing address' });
  const qbInput = makeInput({ value: existing.qbCustomerId || '', placeholder: 'Optional' });

  // Sites: at least one, each an address plus an optional label. Existing sites
  // carry an id which we preserve so jobs keep referencing the same site.
  const makeSiteRow = (site, remove) => {
    const label = makeInput({ value: site.label || '', placeholder: 'Label (e.g. Main house)' });
    const address = makeInput({ value: site.address || '', required: true, placeholder: 'Site address' });
    const id = site.id || null;
    const el = h('div', { class: 'site-row' }, [
      h('div', { class: 'site-row-fields' }, [label, address]),
      button('×', { small: true, onclick: remove, 'aria-label': 'Remove site' }),
    ]);
    const read = () => {
      const addr = address.value.trim();
      if (!addr) return null;
      const out = { address: addr, label: label.value.trim() };
      if (id) out.id = id;
      return out;
    };
    return { el, read };
  };

  const initialSites = (existing.sites && existing.sites.length ? existing.sites : [{}]);
  const sites = repeatableList({ initial: initialSites, makeRow: makeSiteRow, addLabel: 'Add site' });

  const err = errorSlot();

  const bodyNodes = [
    field('Name', nameInput),
    field('Phone', phoneInput),
    field('Email', emailInput),
    field('Billing address', billingInput),
    sectionHeading('Sites'),
    muted('At least one site address is required.'),
    sites.el,
    field('QuickBooks customer ID', qbInput, { hint: 'Optional — links to an existing QuickBooks customer.' }),
    err,
  ];

  const read = () => {
    const payload = {
      name: nameInput.value.trim(),
      contact: { phone: phoneInput.value.trim(), email: emailInput.value.trim() },
      billingAddress: billingInput.value.trim(),
      sites: sites.values(),
      qbCustomerId: qbInput.value.trim() || null,
    };
    return payload;
  };

  const validate = (payload) => {
    if (!payload.name) return 'Please enter the client name.';
    if (!payload.sites.length) return 'Please add at least one site address.';
    return null;
  };

  return { bodyNodes, read, validate, err };
}

// --- Create ----------------------------------------------------------------

export function clientNewScreen() {
  const { bodyNodes, read, validate, err } = clientForm();
  const submit = button('Create client', { variant: 'primary' });

  submit.addEventListener('click', async () => {
    const payload = read();
    const problem = validate(payload);
    if (problem) return setError(err, problem);
    const created = await runAction(submit, () => api.post('/clients', payload), {
      busyLabel: 'Creating…', errSlot: err,
    });
    if (created && created.id) goTo(clientHash(created.id));
  });

  const form = h('div', { class: 'form' }, [...bodyNodes, buttonRow([submit, linkButton('Cancel', routeToHash({ section: 'clients' }))])]);
  return screenEl('New client', form, {
    section: 'clients',
    backHref: routeToHash({ section: 'clients' }),
    backLabel: 'Clients',
  });
}

// --- Detail (view + in-place edit) -----------------------------------------

export function clientDetailScreen(id) {
  const body = h('div');

  const refresh = () =>
    runAsync(
      body,
      () => Promise.all([api.get(`/clients/${encodeURIComponent(id)}`), api.get(`/clients/${encodeURIComponent(id)}/jobs`)]),
      ([client, jobsRes]) => renderView(client, (jobsRes && jobsRes.items) || []),
      { loadingMessage: 'Loading client…' },
    );

  const renderView = (client, jobs) => {
    const sitesById = new Map((client.sites || []).map((s) => [s.id, s]));
    const wrap = h('div');

    const sitesList = (client.sites || []).map((s) =>
      h('div', { class: 'detail-row' }, [
        h('span', { class: 'detail-label', text: s.label || 'Site' }),
        h('span', { class: 'detail-value', text: s.address }),
      ]),
    );

    const jobRows = jobs.length
      ? jobs.map((j) =>
          listRow({
            href: routeToHash({ section: 'jobs', action: 'view', id: packJobId(client.id, j.id) }),
            title: j.description ? truncate(j.description, 60) : 'Job',
            subtitle: (sitesById.get(j.siteId) || {}).label || (sitesById.get(j.siteId) || {}).address || '',
            meta: [statusBadge(j.status), h('span', { class: 'list-row-date', text: shortDate(j.updatedAt) })],
          }),
        )
      : [muted('No jobs recorded for this client yet.')];

    const editBtn = button('Edit', {});
    const archiveBtn = button(client.archived ? 'Unarchive' : 'Archive', {});
    const actionErr = errorSlot();

    editBtn.addEventListener('click', () => mount(body, renderEdit(client)));
    archiveBtn.addEventListener('click', async () => {
      await runAction(archiveBtn, () => api.put(`/clients/${encodeURIComponent(id)}`, { archived: !client.archived }), {
        busyLabel: 'Saving…', errSlot: actionErr,
      });
      refresh();
    });

    mount(
      wrap,
      h('div', {}, [
        card([
          detailList([
            ['Phone', client.contact && client.contact.phone],
            ['Email', client.contact && client.contact.email],
            ['Billing', client.billingAddress],
            ['QuickBooks', client.qbCustomerId],
            ['Status', client.archived ? statusBadge('archived') : statusBadge('active')],
          ]),
        ], { title: client.name }),
        card([...sitesList], { title: 'Sites' }),
        buttonRow([editBtn, archiveBtn, linkButton('＋ New job', routeToHash({ section: 'jobs', action: 'new' }))]),
        actionErr,
        sectionHeading('Jobs'),
        h('div', { class: 'list' }, jobRows),
      ]),
    );
    return wrap;
  };

  const renderEdit = (client) => {
    const { bodyNodes, read, validate, err } = clientForm(client);
    const save = button('Save changes', { variant: 'primary' });
    const cancel = button('Cancel', {});
    cancel.addEventListener('click', () => refresh());
    save.addEventListener('click', async () => {
      const payload = read();
      const problem = validate(payload);
      if (problem) return setError(err, problem);
      const updated = await runAction(save, () => api.put(`/clients/${encodeURIComponent(id)}`, payload), {
        busyLabel: 'Saving…', errSlot: err,
      });
      if (updated) refresh();
    });
    return h('div', { class: 'form' }, [...bodyNodes, buttonRow([save, cancel])]);
  };

  refresh();
  return screenEl('Client', body, {
    section: 'clients',
    backHref: routeToHash({ section: 'clients' }),
    backLabel: 'Clients',
  });
}

function truncate(text, max) {
  const s = String(text || '');
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}
